import i18next from 'i18next'
import React from 'react'
import { Form } from 'react-bootstrap'
import { Updater } from 'widgets-for-react'
import { FormControls, formClasses } from '../sharedLayout/forms'
import { getClassName } from '../../utils/utils'
import { AZNavigationState, azNavigationStateUpdaters, navigateToState } from './azNavigationState'

export type AZNavigationLayoutProps = {
  title: string
  intro?: string
  enabledLetters: string[]
  showFilter?: boolean
  filter?: PaginationFilterLayoutProps
  filter2?: PaginationFilterLayoutProps
  search?: SearchInputLayoutProps
  secondPagination?: boolean
}

type PaginationFilterLayoutProps = {
  allLabel?: string
  label?: string
  options: { key: string; title: string }[]
}

type SearchInputLayoutProps = {
  kind: 'navigation' | 'search-results' | 'pagination-overview'
  label?: string
  placeholder: string
}

export const AZNavigationLayout = (
  props: AZNavigationLayoutProps & {
    onDone: (u: Updater<AZNavigationState>) => void
    state: AZNavigationState
  }
) => {
  const letters = [
    'A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'G',
    'H',
    'I',
    'J',
    'K',
    'L',
    'M',
    'N',
    'O',
    'P',
    'Q',
    'R',
    'S',
    'T',
    'U',
    'V',
    'W',
    'X',
    'Y',
    'Z',
  ]

  const submitStateChange = (change: Updater<AZNavigationState>) => {
    const nextState = azNavigationStateUpdaters.submit(change(props.state))
    props.onDone(() => nextState)
    navigateToState(nextState)
  }

  return (
    <nav className="pagination--az gutter--m container--m" aria-label={i18next.t('a11y:navLabelPagination')}>
      {(props.title || props.intro) && (
        <header className="pagination__header">
          <h2 className="pagination__title title--l">{props.title}</h2>
          {props.intro && (
            <p className="pagination__intro text--styled" dangerouslySetInnerHTML={{ __html: props.intro }} />
          )}
        </header>
      )}

      <div className="pagination__inner">
        <ul className="pagination__list" aria-hidden={props.enabledLetters.length == 0}>
          {letters.map((letter) =>
            !props.enabledLetters.includes(letter) ? (
              <li className="pagination__item pagination__item--disabled" aria-hidden="true" key={letter}>
                <span className="pagination__letter">{letter}</span>
              </li>
            ) : (
              <li className="pagination__item" key={letter}>
                <a href={`#pagination-${letter}`} className="pagination__link">
                  <span>{letter}</span>
                </a>
              </li>
            )
          )}
        </ul>
      </div>

      {props.showFilter && props.filter && props.search && (
        <fieldset className="pagination__filter filter--pagination">
          <legend className="sr-text">{i18next.t('misc:specialistOverviewFilterTitle')}</legend>
          {props.secondPagination == true ? (
            <>
              <PaginationFilterLayout
                {...props.filter}
                controlId=''
                onChange={(k) => submitStateChange(azNavigationStateUpdaters.updateFilter(k))}
                current={props.state.filter2}
                secondPagination={true}
              />
              <SearchInputLayout
                {...props.search}
                onDone={() =>props.onDone}
                onSubmit={() => submitStateChange((s) => s)}
                current={props.state.query}
                secondPagination={true}
              />
            </>
          ) : (
            <>
              <PaginationFilterLayout controlId={'1'} {...props.filter} onChange={(k: string) => submitStateChange(azNavigationStateUpdaters.updateFilter(k))} current={props.state.filter} />
              {props.filter2 && <PaginationFilterLayout controlId={'2'} {...props.filter2} onChange={(k: string) => submitStateChange(azNavigationStateUpdaters.updateFilter2(k))} current={props.state.filter2} />}
              <SearchInputLayout {...props.search} onDone={props.onDone} onSubmit={() => submitStateChange((s) => s)} current={props.state.query} />
            </>
          )}
        </fieldset>
      )}
    </nav>
  )
}

const PaginationFilterLayout = (
  props: PaginationFilterLayoutProps & {
    controlId: string
    onChange: (selected: string) => void
    current: string
    secondPagination?: boolean
  }
) => {
  return (
    <div className="pagination__control">
      <FormControls.FormSelectInGroup
        controlId={`pagination-select-${props.controlId}`}
        label={props.label ? props.label : ''}
        onChange={(k) => props.onChange(k)}
        required={true}
        value={props.current}
      >
        <option value="">{props.allLabel}</option>
        <>
          {props.options.map((option) => (
            <option key={option.key} value={option.key}>
              {option.title}
            </option>
          ))}
        </>
      </FormControls.FormSelectInGroup>
    </div>
  )
}

export const SearchInputLayout = (
  props: SearchInputLayoutProps & {
    onDone: (u: Updater<AZNavigationState>) => void
    onSubmit: () => void
    current: string
    secondPagination?: boolean
  }
) => (
  <div className="pagination__control">
    <form
      className="searchbar"
      onSubmit={(e) => {
        e.preventDefault()
        props.onSubmit()
      }}
    >
      <Form.Group controlId={`${props.kind}-search"`} className={getClassName(formClasses, 'formGroupClassName', '')}>
        {props.label && (
          <Form.Label className={getClassName(formClasses, 'formLabelClassName', '')}>{props.label}</Form.Label>
        )}

        <div className="searchbar__input">
          <Form.Control
            type="search"
            inputMode="search"
            placeholder={props.placeholder}
            onChange={(e) => {
              props.secondPagination == true
                ? props.onDone(azNavigationStateUpdaters.updateQuery2(e.currentTarget.value))
                : props.onDone(azNavigationStateUpdaters.updateQuery(e.currentTarget.value))
            }}
            className={getClassName(formClasses, 'formInputClassName', '')}
            value={props.current}
          />
          <button className="searchbar__button" title={i18next.t('common:search')} type="submit">
            <span className="sr-text">{i18next.t('common:search')}</span>
            <svg aria-hidden="true">
              <use xlinkHref="#icon--SG-zoeken" />
            </svg>
          </button>
        </div>
      </Form.Group>
    </form>
  </div>
)
